import { IPagination, IPaginationDTO } from '@/models/pagination.model';
import { IManager } from '@/models/admins.model';

export enum OrdersEndpoints {
  ORDERS = '/orders',
  ORDER_PRODUCTS = '/products/autocomplete-with-prices',
  ORDER_CHANGE_REGION = '/change-region',
}

export enum OrdersStatus {
  NEW = 'new',
  PROCESSING = 'processing',
  TRANSFERRED = 'transferred',
  ACCEPTED = 'approved',
  READY_FOR_TTN = 'ready_for_ttn',
  AWAITING = 'awaiting',
  RESERVE = 'reserve',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum OrderCancellationReason {
  CLIENT_NO_CONTACT_3_DAYS = 'client_no_contact_3_days',
  RECIPIENT_REFUSED_NOVA_POSHTA = 'recipient_refused_nova_poshta',
  PRODUCT_UNAVAILABLE_SUPPLIER = 'product_unavailable_supplier',
  PRODUCT_MISMATCH_SPECS = 'product_mismatch_specs',
  CLIENT_REFUSED_NO_REASON = 'client_refused_no_reason',
  SUPPLIER_SHIPMENT_OVERDUE = 'supplier_shipment_overdue',
  TEST_OR_DUPLICATE_ORDER = 'test_or_duplicate_order',
  PRODUCT_UNAVAILABLE_GOST_PICKUP = 'product_unavailable_gost_pickup',
  NOVA_POSHTA_DELIVERY_EXPENSIVE = 'nova_poshta_delivery_expensive',
  ONLINE_PAYMENT_ERROR = 'online_payment_error',
  SHIPMENT_DATES_NOT_SUITABLE = 'shipment_dates_not_suitable',
  PRODUCT_NOT_SUITABLE_FOR_CLIENT = 'product_not_suitable_for_client',
  ORDER_NO_LONGER_RELEVANT = 'order_no_longer_relevant',
  CLIENT_REFUSED_PREPAYMENT = 'client_refused_prepayment',
  MULTIPLE_TTN_NOT_SUITABLE = 'multiple_ttn_not_suitable',
  INCORRECT_CLIENT_PHONE = 'incorrect_client_phone',
  OTHER = 'other',
}

export enum OrdersPaymentStatus {
  AWAITING_PAYMENT = 'awaiting_payment',
  AWAITING_PREPAYMENT = 'awaiting_prepayment',
  PREPAID = 'prepaid',
  PAID = 'paid',
  CASH_ON_DELIVERY = 'cash_on_delivery',
  CANCELLED = 'cancelled',
  PAYMENT_REFUND = 'payment_refund',
  PREPAYMENT_REFUND = 'prepayment_refund',
  AWAITING_ADDITIONAL_PAYMENT = 'awaiting_additional_payment',
  REQUIRES_PARTIAL_REFUND = 'requires_partial_refund',
}

export enum PartnerConfirmationStatus {
  CONFIRMED = 'confirmed',
  PARTIAL = 'partial',
  UNCONFIRMED = 'unconfirmed',
}

export enum OrdersDeliveryType {
  GOST = 'gost',
  NOVA_POSHTA = 'nova_poshta',
  PICKUP = 'pickup',
}

export enum OrdersSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum OrdersUtmSource {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  GOOGLE = 'google',
  TELEGRAM = 'telegram',
  OTHERS = 'others',
  ORGANIC = 'organic',
}

export enum OrderPayments {
  GOST = 'gost',
  LIQPAY = 'liqpay',
}

export enum PaymentTypes {
  PAYMENT_NOW = 'payment_now',
  CASH_ON_DELIVERY = 'cash_on_delivery',
  PREPAYMENT_AFTER_CALL = 'prepayment_after_call',
  PAYMENT_AFTER_CALL = 'payment_after_call',
  PREPAYMENT_NOW = 'prepayment_now',
}

export enum OrderPaymentChannel {
  CASH = 'cash',
  BANK_TERMINAL = 'bank_terminal',
  INTERNET_ACQUIRING = 'internet_acquiring',
  BANK_TRANSFER = 'bank_transfer',
  NOVAPAY = 'novapay',
}

export enum BuildingTypes {
  FLAT = 'flat',
  PRIVATE = 'private',
}

export enum LegalType {
  FOP = 'fop',
  TOV = 'tov',
}

export enum AuthorRole {
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  PARTNER = 'partner',
  PARTNER_MANAGER = 'partner-manager',
}

export interface IUtmTouchBlock {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
}

export interface IOrder {
  id: string;
  number: number; // serial number of the order
  total_price: string;
  contact_name: string;
  contact_phone: string;
  orders_count?: number;
  delivery_type: OrdersDeliveryType;
  status: OrdersStatus;
  payment_status: OrdersPaymentStatus;
  payment_date?: string | null;
  created_at: string;
  updated_at: string;
  stores: string[];
  awaiting_since: string | null;
  manager: IManager | null;
  /** Last-touch marketing source (utm_source) */
  utm_source?: string | null;
  payment_channel?: OrderPaymentChannel | null;
  refund_date?: string | null;
  edited_after_fulfillment?: boolean;
  deleted_at?: string | null;
  partner_confirmation_status?: PartnerConfirmationStatus;
  partner_confirmed_count?: number;
  partner_confirmed_total?: number;
}

export interface IOrderDelivery {
  id: string;
  delivery: OrdersDeliveryType;
  region_id: string;
  settlement: string;
  settlement_label?: string | null;
  settlement_id: string;
  building_type: BuildingTypes;
  address: string;
  delivery_department: string;
  delivery_warehouse_id: string;
  floor: number;
  flat_number: string;
  freight_elevator: boolean;
  comment: string;
  error?: boolean;
  error_message?: string;
}

export interface IOrderPaymentMethod {
  payment_method: OrderPayments;
  prepayment_amount: string;
}

export interface IOrderNovaPoshtaDepartment {
  id: string;
  name: string;
}

export interface IOrderProductStock {
  id: string;
  product_id: string;
  region: string;
  available: boolean;
  quantity: number;
  price: string;
  discount_price: string;
}

export interface IOrderProduct {
  id: string;
  external_id?: string | null;
  name: string;
  sku: string;
  quantity: number;
  price: number;
  total_price: number;
  stock_quantity: number;
  total_stock_quantity: number;
  category: string;
  category_id?: string | null;
  category_deleted_at?: string | null;
  photo: string;
  store: string;
  deleted_at: string | null;
  active: true;
  stock: IOrderProductStock | null;
  is_custom_order?: boolean;
  package_quantity_snapshot: number;
  unit_code_snapshot: string;
}

export interface IOrderComment {
  id: string;
  commentable_id: string;
  commentable_type: string;
  author_id: string;
  author_name: string;
  author_role: AuthorRole;
  content: string;
  type?: CommentType;
  created_at: string;
}

export enum CommentType {
  DEFAULT = 'default',
  CANCELLATION = 'cancellation',
}

export interface ISubOrder {
  id: string;
  store_id: string;
  store_title: string;
  delivery: IOrderDelivery;
  payment_method: IOrderPaymentMethod;
  products: IOrderProduct[];
  comments: IOrderComment[];
  delivery_price: string;
  proportions: string | null;
  total_price: string;
  total_weight: string;
  volume_weight: string;
  is_partner_confirmed?: boolean;
  partner_confirmed_at?: string | null;
  partner_confirmed_by?: {
    id: string;
    full_name: string;
  } | null;
}

export interface IPaymentTransaction {
  date: string;
  type: string;
  amount: number;
  receipt_url: string | null;
  fiscal_receipt_urls: string[] | null;
}

export type FiscalReceiptStatus = 'not_applicable' | 'pending' | 'done' | 'failed';

export interface IFiscalReceiptItem {
  name: string | null;
  sku: string | null;
  product_id: string | null;
  quantity: number;
  price: number;
  sum: number;
  sum_with_vat: number;
}

export interface IFiscalReceipt {
  id: string;
  checkbox_receipt_id: string;
  related_checkbox_receipt_id: string | null;
  type: string | null;
  payment_method: string | null;
  legal_type: LegalType | null;
  status: FiscalReceiptStatus | null;
  fiscal_code: string | null;
  receipt_url: string | null;
  error: string | null;
  created_at: string | null;
  cash_register: { id: string; name: string } | null;
  items: IFiscalReceiptItem[];
}

export interface IIssueOrderReturnLine {
  index: number;
  quantity: number;
}

export interface IIssueOrderReturnItem {
  receipt_id: string;
  lines: IIssueOrderReturnLine[];
}

export interface IIssueOrderReturnDTO {
  order_id: string;
  all?: boolean;
  items?: IIssueOrderReturnItem[];
}

export interface IPaymentsInfo {
  legalType: LegalType;
  prepayment: number;
  prepaymentBalance: number;
  paidAmount: number;
  paidPrepayment: number;
  paymentBalance: number;
  refundAmount: number;
  transactions?: IPaymentTransaction[];
}

export interface IRegionsLocale {
  uk: {
    name: string;
  };
  ru: {
    name: string;
  };
}

export interface IOrderRegions {
  id: string;
  locale: IRegionsLocale;
}

export interface IStatusHistoryItem {
  status: OrdersStatus;
  date: string | null;
  value: string | null;
}

export interface ICompositionProductDiff {
  product_id: string;
  sku?: string | null;
  old_qty: number;
  new_qty: number;
}

export interface ICompositionChangeLog {
  id: string;
  reason: string;
  old_total: number;
  new_total: number;
  diff: number;
  old_payment_status: OrdersPaymentStatus | null;
  new_payment_status: OrdersPaymentStatus | null;
  products_diff: ICompositionProductDiff[];
  admin: IManager | null;
  created_at: string;
}

export interface IOrderDetails extends Omit<IOrder, 'delivery_type' | 'awaiting_since'> {
  contact_email: string;
  description: string;
  sub_orders: ISubOrder[];
  delivery: IOrderDelivery | null;
  payment_method: IOrderPaymentMethod;
  payment_type?: PaymentTypes;
  delivery_price: string;
  proportions: string | null;
  total_price: string;
  total_weight: string;
  volume_weight: string;
  total_discount: number;
  region_id?: string | null;
  region: IOrderRegions | null;
  payments_info: IPaymentsInfo[];
  status_history: IStatusHistoryItem[];
  composition_change_logs?: ICompositionChangeLog[];
  fiscal_receipts?: IFiscalReceipt[];
  cancellation_reason?: OrderCancellationReason | null;
  cancellation_reason_text?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_first_touch?: IUtmTouchBlock | null;
  utm_last_touch?: IUtmTouchBlock | null;
}

// ----------------- DTOs ----------------

export interface IGetOrdersListDTO extends Partial<IPaginationDTO> {
  limit?: number;
  id?: string;
  number?: number;
  contact_name?: string;
  contact_phone?: string;
  date_from?: string;
  date_to?: string;
  payment_date_from?: string;
  payment_date_to?: string;
  status?: OrdersStatus;
  payment_status?: OrdersPaymentStatus;
  payment_channel?: OrderPaymentChannel;
  delivery_type?: OrdersDeliveryType;
  sort_direction?: OrdersSortDirection;
  store_ids?: string[];
  manager_id?: string;
  utm_source?: string;
  partner_confirmation_status?: PartnerConfirmationStatus;
}

export interface IGetOrderDetailsParams {
  id: string;
}

export interface IChangeOrderManagerDTO {
  order_id: string;
  manager_id: string;
  utm_source?: string;
}

export interface IUpdateOrderDeliveryDTO {
  order_id: string;
  region_id: string;
  delivery_type: OrdersDeliveryType;
  settlement_id: string;
  address?: string;
  building_type?: BuildingTypes;
  floor?: number;
  flat_number?: string;
  freight_elevator?: '1' | '0';
  delivery_department?: string;
  delivery_warehouse_id?: string;
  comment?: string;
}

export interface IUpdateOrderClientDTO {
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  description?: string;
}

export interface IUpdateOrderPaymentDTO {
  order_id: string;
  payment_status: OrdersPaymentStatus;
}

export interface IUpdateOrderPaymentChannelDTO {
  order_id: string;
  payment_channel: OrderPaymentChannel;
}

export interface IUpdateOrderProductsDTO {
  order_id: string;
  products: {
    id: string;
    quantity: number;
  }[];
  add_new?: boolean;
  reason?: string;
}

export interface IRecordAdditionalPaymentDTO {
  order_id: string;
  amount: number;
  payment_channel: OrderPaymentChannel;
}

export interface IRecordRefundDTO {
  order_id: string;
  amount: number;
}

export interface IAddOrderCommentDTO {
  order_id: string;
  subOrder_id: string;
  comment: string;
}

export interface IChangeOrderStatusDTO {
  order_id: string;
  status: OrdersStatus;
  cancellation_reason?: OrderCancellationReason;
  cancellation_reason_text?: string;
}

export interface IChangeOrderRegionDTO {
  order_id: string;
  region_id: string;
}

export interface IGetOrderProductsDTO extends Partial<IPaginationDTO> {
  name?: string;
  sku?: string;
  id?: string;
  store?: string;
  category?: string;
  category_ids?: string[];
  active?: '0' | '1';
  limit?: number;
  partner_ids?: string[];
}

export interface IGetNovaPoshtaDepartmentsDTO {
  name: string;
  order: string;
  settlementFrom_id: string;
  settlementTo_id: string;
}

export interface IOrderListMeta extends IPagination {
  orders_total?: number;
  revenue_total?: number;
}

// ------------ Responses --------------

export interface IOrdersListResponse {
  data: IOrder[];
  meta: IOrderListMeta;
}

export interface IOrderDetailsResponse {
  data: IOrderDetails;
}

export interface IAddOrderCommentResponse {
  data: IOrderComment;
}

export interface IGetOrderProductsResponse {
  data: IOrderProduct[];
  meta: IPagination;
}

export interface IGetNovaPoshtaDepartmentsResponse {
  data: IOrderNovaPoshtaDepartment[];
}

export interface IGetNewOrdersCountResponse {
  data: {
    count: number;
  };
}
